import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiClient } from '@/shared/api/client'
import { LoadingSpinner } from '@/shared/components/ui/LoadingSpinner'
import { API_ENDPOINTS } from '@/shared/lib/constants'
import { formatDate } from '@/shared/lib/utils'
import type { CustodyPublicResponse } from '@/shared/types/api.types'

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  carta_documento: 'Carta Documento',
  contrato: 'Contrato',
  poder: 'Poder',
  acta: 'Acta',
  resolucion: 'Resolución',
  otro: 'Otro',
}

const JURISDICTION_LABELS: Record<string, string> = {
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  EC: 'Ecuador',
  MX: 'México',
  PE: 'Perú',
  ES: 'España',
  US: 'Estados Unidos',
}

export default function CustodyLookup() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [custody, setCustody] = useState<CustodyPublicResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const verificationUrl = typeof window !== 'undefined' ? window.location.href : ''

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(verificationUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const input = document.createElement('input')
      input.value = verificationUrl
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  useEffect(() => {
    const fetchCustody = async () => {
      if (!id) {
        setError('ID de custodia no proporcionado')
        setLoading(false)
        return
      }

      try {
        const response = await apiClient.get<CustodyPublicResponse>(
          `${API_ENDPOINTS.GET_CUSTODY}/${id}`
        )
        setCustody(response.data)
      } catch (err: any) {
        if (err.status_code === 404) {
          setError('Registro de custodia no encontrado. El ID proporcionado no existe en nuestros registros.')
        } else {
          setError('Error al cargar el registro de custodia. Por favor, intente nuevamente.')
        }
        console.error('Error fetching custody:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchCustody()
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-legal-900">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !custody) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-legal-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-legal-800 rounded-lg shadow-lg p-8">
          <div className="text-center">
            <span className="text-6xl">❌</span>
            <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">Error</h2>
            <p className="mt-2 text-gray-600 dark:text-legal-300">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-6 px-6 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
            >
              Volver al Inicio
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-legal-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Validación Pública de Custodia
          </h1>
          <p className="mt-2 text-gray-600 dark:text-legal-300">
            Consulta independiente de registro de custodia documental
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white dark:bg-legal-800 rounded-lg shadow-lg border-l-4 border-blue-500 p-6 mb-6">
          {/* Status Header */}
          <div className="flex items-center gap-3 mb-6">
            <span className="text-5xl">✅</span>
            <div>
              <h2 className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                Documento Registrado
              </h2>
              <p className="text-sm text-gray-500 dark:text-legal-400">
                Registrado el {formatDate(custody.custody_timestamp)}
              </p>
            </div>
          </div>

          {/* Document Info */}
          {custody.original_filename && (
            <div className="mb-4">
              <p className="text-legal-600 dark:text-legal-400 text-sm font-mono">
                Archivo: {custody.original_filename}
              </p>
            </div>
          )}

          {custody.document_title && (
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-blue-900 dark:text-blue-300">
                {custody.document_title}
              </h3>
            </div>
          )}

          {/* Message Box */}
          <div className="bg-green-50 dark:bg-green-900/30 border-l-4 border-green-500 p-4 mb-6">
            <p className="text-green-800 dark:text-green-200">
              Este documento fue registrado en SignWiz en la fecha indicada.
              El hash SHA-256 permite verificar que cualquier copia del documento
              es idéntica al original registrado.
            </p>
          </div>

          {/* Custody ID */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 dark:text-legal-300 mb-2">
              ID de Custodia
            </h3>
            <code className="block bg-gray-100 dark:bg-legal-700 text-gray-900 dark:text-legal-100 p-3 rounded text-sm break-all font-mono">
              {custody.custody_id}
            </code>
          </div>

          {/* Hash */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 dark:text-legal-300 mb-2">
              Hash del Documento ({custody.hash_algorithm})
            </h3>
            <code className="block bg-gray-100 dark:bg-legal-700 text-gray-900 dark:text-legal-100 p-3 rounded text-sm break-all font-mono">
              {custody.file_hash}
            </code>
            <p className="text-xs text-gray-500 dark:text-legal-400 mt-2">
              Para verificar, calculá el hash SHA-256 de tu documento y comparalo con el de arriba
            </p>
          </div>

          {/* Document Details */}
          {(custody.document_type || custody.file_size_bytes || custody.jurisdiction) && (
            <div className="mb-6 border-t border-gray-200 dark:border-legal-700 pt-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4">
                Detalles del Documento
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {custody.document_type && (
                  <div>
                    <h4 className="font-semibold text-gray-700 dark:text-legal-300 text-sm">
                      Tipo de Documento
                    </h4>
                    <p className="text-gray-600 dark:text-legal-400 text-sm">
                      {DOCUMENT_TYPE_LABELS[custody.document_type] || custody.document_type}
                    </p>
                  </div>
                )}
                {custody.jurisdiction && (
                  <div>
                    <h4 className="font-semibold text-gray-700 dark:text-legal-300 text-sm">
                      Jurisdicción
                    </h4>
                    <p className="text-gray-600 dark:text-legal-400 text-sm">
                      {JURISDICTION_LABELS[custody.jurisdiction] || custody.jurisdiction}
                    </p>
                  </div>
                )}
                {custody.file_size_bytes && (
                  <div>
                    <h4 className="font-semibold text-gray-700 dark:text-legal-300 text-sm">
                      Tamaño del Archivo
                    </h4>
                    <p className="text-gray-600 dark:text-legal-400 text-sm">
                      {(custody.file_size_bytes / 1024).toFixed(1)} KB
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Legal Context */}
          {(custody.legal_context || custody.recipient_name) && (
            <div className="mb-6 border-t border-gray-200 dark:border-legal-700 pt-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4">
                Contexto Legal
              </h3>
              {custody.recipient_name && (
                <div className="mb-3">
                  <h4 className="font-semibold text-gray-700 dark:text-legal-300 text-sm">
                    Destinatario
                  </h4>
                  <p className="text-gray-600 dark:text-legal-400">
                    {custody.recipient_name}
                    {custody.recipient_id && ` - ${custody.recipient_id}`}
                  </p>
                </div>
              )}
              {custody.legal_context && (
                <div>
                  <h4 className="font-semibold text-gray-700 dark:text-legal-300 text-sm">
                    Contexto
                  </h4>
                  <p className="text-gray-600 dark:text-legal-400">
                    {custody.legal_context}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          {custody.document_description && (
            <div className="mb-6 border-t border-gray-200 dark:border-legal-700 pt-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                Descripción
              </h3>
              <p className="text-gray-600 dark:text-legal-400">
                {custody.document_description}
              </p>
            </div>
          )}
        </div>

        {/* URL de verificación */}
        <div className="bg-white dark:bg-legal-800 rounded-lg shadow-lg p-6 mb-6">
          <h3 className="font-semibold text-gray-700 dark:text-legal-300 mb-2">
            URL de verificación
          </h3>
          <p className="text-sm text-gray-500 dark:text-legal-400 mb-3">
            Usa esta URL para volver a consultar el estado de custodia de este documento en cualquier momento.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-gray-100 dark:bg-legal-700 text-gray-900 dark:text-legal-100 px-3 py-2 rounded text-sm break-all font-mono">
              {verificationUrl}
            </code>
            <button
              onClick={handleCopy}
              className={`flex-shrink-0 px-4 py-2 rounded-lg transition-colors font-medium text-sm ${
                copied
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50'
              }`}
            >
              {copied ? 'Copiado' : 'Copiar'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <button
            onClick={() => navigate('/custody')}
            className="px-6 py-3 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors font-medium"
          >
            Registrar Otro Documento
          </button>
        </div>

        {/* Info Footer */}
        <div className="mt-8 text-center text-sm text-gray-500 dark:text-legal-400">
          <p>
            Esta consulta es pública y puede ser realizada por cualquier persona con el ID de custodia.
          </p>
          <p className="mt-2">SignWiz - Evidencia Digital Inteligente</p>
          <p className="mt-2">
            Desarrollado por{' '}
            <a
              href="https://arteclaw.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              Arteclaw
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
